import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import { Input, Button } from '../ui/Kit';
import Spinner from '../ui/Spinner';
import { api } from '../../lib/api';
import { useToast } from '../ui/Toast';

export default function SelectLeadModal({
  isOpen,
  onClose,
  agencyId,
  onSelect,
  title = 'Select a lead',
  description = 'Choose a lead to schedule an appointment.',
  allowQuickCreate = false,
}) {
  const { addToast } = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
  });
  const [createErrors, setCreateErrors] = useState({});

  useEffect(() => {
    if (!isOpen || !agencyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await api.leads.getAll({});
        if (!cancelled) setLeads(rows || []);
      } catch (e) {
        if (!cancelled) {
          addToast({ type: 'error', title: 'Could not load leads', message: e.message });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, agencyId, addToast]);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setShowCreate(false);
      setCreateForm({ first_name: '', last_name: '', phone: '' });
      setCreateErrors({});
    }
  }, [isOpen]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads.slice(0, 50);
    return leads
      .filter((l) => {
        const name = `${l.first_name || ''} ${l.last_name || ''}`.toLowerCase();
        return name.includes(q) || (l.phone || '').includes(q) || (l.email || '').toLowerCase().includes(q);
      })
      .slice(0, 50);
  }, [leads, search]);

  const handleSelect = (lead) => {
    onSelect(lead);
    onClose();
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!createForm.first_name.trim()) errors.first_name = 'First name is required';
    if (!createForm.phone.trim()) errors.phone = 'Phone is required';
    setCreateErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setCreating(true);
    try {
      const newLead = await api.leads.create({
        first_name: createForm.first_name.trim(),
        last_name: createForm.last_name.trim(),
        phone: createForm.phone.trim(),
        lead_type: 'fresh',
        lead_source: 'manual',
        agency_id: agencyId,
      });
      addToast({ type: 'success', title: 'Lead created' });
      handleSelect(newLead);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to create lead', message: err?.message });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} className="max-w-md">
      <p className="text-sm text-gray-500 mb-4">{description}</p>
      {!showCreate && (
        <>
          <Input
            placeholder="Search by name, phone, or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-4"
          />
          {loading ? (
            <div className="flex items-center justify-center py-10 gap-2 text-gray-500 text-sm">
              <Spinner className="text-primary" />
              Loading leads…
            </div>
          ) : (
            <ul className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <li className="px-4 py-6 text-sm text-gray-500 text-center">No leads found</li>
              ) : (
                filtered.map((lead) => {
                  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'Unnamed lead';
                  return (
                    <li key={lead.id}>
                      <button
                        type="button"
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                        onClick={() => handleSelect(lead)}
                      >
                        <p className="text-sm font-medium text-gray-900">{name}</p>
                        {lead.phone && <p className="text-xs text-gray-500 mt-0.5">{lead.phone}</p>}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          )}
        </>
      )}

      {allowQuickCreate && showCreate && (
        <form onSubmit={handleCreateSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">First name</label>
            <Input
              value={createForm.first_name}
              onChange={(e) => setCreateForm((p) => ({ ...p, first_name: e.target.value }))}
              autoFocus
            />
            {createErrors.first_name && (
              <p className="text-xs text-red-500 mt-1">{createErrors.first_name}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Last name</label>
            <Input
              value={createForm.last_name}
              onChange={(e) => setCreateForm((p) => ({ ...p, last_name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
            <Input
              value={createForm.phone}
              onChange={(e) => setCreateForm((p) => ({ ...p, phone: e.target.value }))}
            />
            {createErrors.phone && (
              <p className="text-xs text-red-500 mt-1">{createErrors.phone}</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>
              Back
            </Button>
            <Button type="submit" disabled={creating}>
              {creating ? (
                <>
                  <Spinner className="mr-2" />
                  Creating…
                </>
              ) : (
                'Create & continue'
              )}
            </Button>
          </div>
        </form>
      )}

      {!showCreate && (
        <div className="mt-4 flex items-center justify-between gap-2">
          {allowQuickCreate ? (
            <button
              type="button"
              className="text-sm font-medium text-primary hover:underline"
              onClick={() => setShowCreate(true)}
            >
              Create new lead
            </button>
          ) : (
            <span />
          )}
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      )}
    </Modal>
  );
}
